import React, { useState, useEffect, useMemo } from 'react';

import { addYears } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

import { useSiteMetadata } from '@/hooks/useSiteMetadata';
import { useAsyncFunction } from '@/hooks/useAsyncFunction';
import { Button, OutlineButton } from '@/components/Button';
import { Loader2 } from 'lucide-react';

declare global {
  interface Window {
    dataLayer: any;
  }
}

export const allowCookies = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  /* eslint-disable */
  // @ts-ignore // Couldn't find a way to do this properly
  gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  /* eslint-enable */
};

export const denyCookies = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  /* eslint-disable */
  // @ts-ignore // Couldn't find a way to do this properly
  gtag('consent', 'update', {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  /* eslint-enable */
};

export const CookieConsent: React.FunctionComponent = () => {
  const cookies = useMemo(() => new Cookies(), []);
  const [showConsentNotification, setShowConsentNotification] = useState<boolean | undefined>(
    undefined,
  );
  const { t } = useTranslation();
  const { cookieDomain } = useSiteMetadata();

  enum CookieNames {
    FULL_CONSENT = 'aatos-cookie-consent',
    STRICTLY_NECESSARY = 'aatos-necessary-cookies-only',
  }

  const updateConsent = () => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'update_consent',
    });
  };

  // Send tag manager event to datalayer
  if (cookies.get(CookieNames.FULL_CONSENT)) {
    allowCookies();
  }

  useEffect(() => {
    if (
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/terms-of-service'
    ) {
      setShowConsentNotification(false);
    } else {
      setShowConsentNotification(
        !cookies.get(CookieNames.FULL_CONSENT) && !cookies.get(CookieNames.STRICTLY_NECESSARY),
      );
    }
  }, [cookies, CookieNames]);

  const setCookie = (cookieName: CookieNames) => {
    const expirationDate = addYears(new Date(), 1);

    cookies.set(cookieName, 'true', {
      path: '/',
      expires: expirationDate,
      domain: cookieDomain,
    });
    setShowConsentNotification(false);
  };

  const getApiUrl = () => {
    if (cookieDomain === 'localhost') {
      return 'https://sakari.cookie-api.bind.dev.booris.co.uk';
    }
    return 'https://cookie-api.bind.aatos.app';
  };

  const { asyncFn: setCookieConsent, loading: setCookieConsentLoading } = useAsyncFunction(
    async () => {
      cookies.remove(CookieNames.STRICTLY_NECESSARY, {
        path: '/',
        domain: cookieDomain,
      });
      // ATM we don't wait for the API call to finish
      fetch(`${getApiUrl()}/set-cookie-consent`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          consentType: 'full',
        }),
      });
      setCookie(CookieNames.FULL_CONSENT);
      allowCookies();
      updateConsent();
    },
  );

  const { asyncFn: setOnlyNecessaryCookies, loading: setOnlyNecessaryCookiesLoading } =
    useAsyncFunction(async () => {
      cookies.remove(CookieNames.FULL_CONSENT, {
        path: '/',
        domain: cookieDomain,
      });
      // ATM we don't wait for the API call to finish
      fetch(`${getApiUrl()}/set-cookie-consent`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          consentType: 'necessary-cookies-only',
        }),
      });
      setCookie(CookieNames.STRICTLY_NECESSARY);
      denyCookies();
      updateConsent();
    });

  return (
    <AnimatePresence>
      {showConsentNotification && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-8 max-w-[512px] w-full mx-4 overflow-hidden"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30,
              }}
            >
              <div className="p-24">
                <h2 className="text-20 font-semibold leading-tight mb-12">
                  {t('cookieConsent.boldText')}
                </h2>
                <p className="text-neutral-700 font-normal">{t('cookieConsent.text')}</p>

                <div className="flex flex-col ds-tw-text-sm mb-12 mt-8">
                  <p className="text-neutral-700 font-normal">
                    <a
                      className="text-purple-dark hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      href={t('cookieConsent.cookiesLink')}
                    >
                      {t('cookieConsent.cookiesText')}
                    </a>
                    {' • '}
                    <a
                      className="text-purple-dark hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      href="https://bind.aatos.app/privacy-policy"
                    >
                      {t('cookieConsent.dataProtectionText')}
                    </a>
                  </p>
                </div>

                <div className="flex flex-row justify-end gap-8">
                  <OutlineButton
                    className="bg-transparent"
                    onClick={() => setOnlyNecessaryCookies()}
                    disabled={setOnlyNecessaryCookiesLoading}
                  >
                    {setOnlyNecessaryCookiesLoading && (
                      <Loader2 className="w-16 h-16 animate-spin mr-4 inline" />
                    )}
                    {t('cookieConsent.confirmPartly')}
                  </OutlineButton>
                  <Button onClick={() => setCookieConsent()} disabled={setCookieConsentLoading}>
                    {setCookieConsentLoading && (
                      <Loader2 className="w-16 h-16 animate-spin mr-4 inline" />
                    )}
                    {t('cookieConsent.confirmAll')}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
